/**
 * Spider Chart pour comparer AS-IS vs TO-BE
 */

interface SpiderChartComparisonProps {
  asIs: Record<string, number>;
  toBe: Record<string, number>;
}

export default function SpiderChartComparison({ asIs, toBe }: SpiderChartComparisonProps) {
  const dimensions = Object.keys(asIs);
  const maxValue = 100;
  const center = 170;
  const radius = 130;
  
  // Calculer les points pour chaque dimension
  const getPoints = (values: Record<string, number>) => {
    return dimensions.map((dim, i) => {
      const angle = (Math.PI * 2 * i) / dimensions.length - Math.PI / 2;
      const value = values[dim] || 0;
      const distance = (value / maxValue) * radius;
      const x = center + Math.cos(angle) * distance;
      const y = center + Math.sin(angle) * distance;
      return { x, y };
    });
  };

  const asIsPoints = getPoints(asIs);
  const toBePoints = getPoints(toBe);

  // Créer les path SVG
  const createPath = (points: { x: number; y: number }[]) => {
    return points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x},${p.y}`).join(' ') + ' Z';
  };

  // Lignes de grille
  const gridLevels = [20, 40, 60, 80, 100];

  return (
    <div className="bg-white rounded-xl p-6 shadow-lg">
      <h3 className="text-xl font-bold text-gray-900 mb-6 text-center">
        Maturité TI - AS-IS vs TARGET
      </h3>
      
      <svg width="340" height="340" viewBox="0 0 340 340" className="mx-auto">
        {/* Grille de fond */}
        {gridLevels.map((level) => {
          const gridRadius = (level / maxValue) * radius;
          const gridPoints = dimensions.map((_, i) => {
            const angle = (Math.PI * 2 * i) / dimensions.length - Math.PI / 2;
            return {
              x: center + Math.cos(angle) * gridRadius,
              y: center + Math.sin(angle) * gridRadius,
            };
          });
          return (
            <path
              key={level}
              d={createPath(gridPoints)}
              fill="none"
              stroke="#e5e7eb"
              strokeWidth="1"
            />
          );
        })}

        {/* Axes */}
        {dimensions.map((_, i) => {
          const angle = (Math.PI * 2 * i) / dimensions.length - Math.PI / 2;
          const x = center + Math.cos(angle) * radius;
          const y = center + Math.sin(angle) * radius;
          return (
            <line
              key={i}
              x1={center}
              y1={center}
              x2={x}
              y2={y}
              stroke="#d1d5db"
              strokeWidth="1"
            />
          );
        })}

        {/* AS-IS (Rouge/Orange) */}
        <path
          d={createPath(asIsPoints)}
          fill="rgba(239, 68, 68, 0.2)"
          stroke="#ef4444"
          strokeWidth="2"
        />

        {/* TO-BE (Vert/Bleu) */}
        <path
          d={createPath(toBePoints)}
          fill="rgba(34, 197, 94, 0.2)"
          stroke="#22c55e"
          strokeWidth="2"
        />

        {/* Points AS-IS */}
        {asIsPoints.map((point, i) => (
          <circle
            key={`as-is-${i}`}
            cx={point.x}
            cy={point.y}
            r="4"
            fill="#ef4444"
          />
        ))}

        {/* Points TO-BE */}
        {toBePoints.map((point, i) => (
          <circle
            key={`to-be-${i}`}
            cx={point.x}
            cy={point.y}
            r="4"
            fill="#22c55e"
          />
        ))}

        {/* Labels des dimensions */}
        {dimensions.map((dim, i) => {
          const angle = (Math.PI * 2 * i) / dimensions.length - Math.PI / 2;
          const labelRadius = radius + 32;
          const x = center + Math.cos(angle) * labelRadius;
          const y = center + Math.sin(angle) * labelRadius;
          
          return (
            <text
              key={dim}
              x={x}
              y={y}
              textAnchor="middle"
              dominantBaseline="middle"
              className="text-[10px] font-medium fill-gray-700"
            >
              {dim}
            </text>
          );
        })}
      </svg>

      {/* Légende */}
      <div className="flex justify-center gap-6 mt-6">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-red-500 rounded-full" />
          <span className="text-sm text-gray-700">AS-IS (État actuel)</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 bg-green-500 rounded-full" />
          <span className="text-sm text-gray-700">TARGET (Cible 3-5 ans)</span>
        </div>
      </div>

      {/* Tableau de comparaison */}
      <div className="mt-6 border border-gray-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left p-3 font-semibold text-gray-900">Dimension</th>
              <th className="text-center p-3 font-semibold text-red-600">AS-IS</th>
              <th className="text-center p-3 font-semibold text-green-600">TARGET</th>
              <th className="text-center p-3 font-semibold text-blue-600">Écart</th>
            </tr>
          </thead>
          <tbody>
            {dimensions.map((dim, idx) => {
              const gap = toBe[dim] - asIs[dim];
              return (
                <tr key={dim} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                  <td className="p-3 font-medium text-gray-900">{dim}</td>
                  <td className="text-center p-3 text-red-600 font-bold">{asIs[dim]}%</td>
                  <td className="text-center p-3 text-green-600 font-bold">{toBe[dim]}%</td>
                  <td className="text-center p-3">
                    <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full font-semibold">
                      +{gap}%
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}







